<template>
  <div class="student-page-wrapper">
    <!-- Header -->
    <StudentHeader />

    <!-- Loading state while checking access -->
    <div
      v-if="isCheckingAccess"
      class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-24 sm:pt-32 lg:pt-44"
    >
      <div class="flex justify-center items-center min-h-[400px]">
        <div class="text-center">
          <div
            class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"
          ></div>
          <p class="text-gray-600">Checking access...</p>
        </div>
      </div>
    </div>

    <!-- Main content - only show if access is granted -->
    <template v-else-if="!accessDenied">
      <!-- Pre-Test Modal -->
      <PreTestModal
        :is-open="showPreTest"
        :session-id="bookedSessionId"
        :session-subject="sessionData.subject"
        :learning-goals="sessionData.learningGoals"
        :materials="sessionData.materials"
        @complete="handlePreTestComplete"
        @close="handlePreTestClose"
      />

      <!-- Main Content -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-24 sm:pt-32 lg:pt-44">
        <!-- Student Status Banner -->
        <StudentStatusBanner :dismissible="true" />

        <!-- Back Button and Title -->
        <div
          class="mb-4 sm:mb-2 flex flex-col items-center sm:flex-row sm:justify-center sm:items-center gap-3 sm:gap-2"
        >
          <button
            @click="$router.go(-1)"
            class="bg-white border-3 border-black rounded-full flex items-center justify-center cursor-pointer shadow-none text-black transition-all duration-300 ease-in-out hover:transform hover:translate-y-[-2px] hover:shadow-[0_4px_8px_rgba(0,0,0,0.1)] flex-shrink-0 sm:mb-0 mb-0 sm:mr-12"
            style="width: 50px; height: 50px; border: 3px solid #000000"
          >
            <svg
              class="w-6 h-6 sm:w-8 sm:h-8 text-black"
              fill="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M11.03 3.97a.75.75 0 0 1 0 1.06l-6.22 6.22H21a.75.75 0 0 1 0 1.5H4.81l6.22 6.22a.75.75 0 1 1-1.06 1.06l-7.5-7.5a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 0 1 1.06 0Z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
          <div class="text-center sm:text-center px-2">
            <h1 class="text-xl sm:text-2xl lg:text-4xl font-extrabold text-gray-900 leading-tight">
              Schedule Your Learning Session
            </h1>
            <p class="text-sm sm:text-base lg:text-lg text-gray-600 mt-1 sm:mt-2">
              Select a convenient date and time to schedule your appointment.
            </p>
          </div>
        </div>

        <!-- Main Content Grid -->
        <div
          class="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mt-6 sm:mt-8 lg:mt-12"
        >
          <!-- Loading State -->
          <div v-if="isLoadingTutor" class="lg:col-span-3 flex justify-center items-center py-20">
            <div class="text-center">
              <div
                class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"
              ></div>
              <p class="text-gray-600">Loading tutor information...</p>
            </div>
          </div>

          <!-- Main Content (shown when not loading) -->
          <template v-else>
            <!-- Left Side - Tutor Profile Card (Sticky) -->
            <div class="lg:col-span-1">
              <div class="lg:sticky lg:top-4">
                <TutorProfileCard :tutor="selectedTutor" />
              </div>
            </div>

            <!-- Right Side - Session Configuration -->
            <div class="lg:col-span-2">
              <!-- Step 1: Date & Time Selection -->
              <DateTimeSelector
                v-if="currentStep === 1"
                :tutor="selectedTutor"
                :current-step="currentStep"
                :selected-date-time="sessionData.dateTime"
                :selected-week-date="sessionData.selectedWeekDate"
                @next-step="handleNextStep"
                @datetime-selected="handleDateTimeSelected"
              />

              <!-- Step 2: Session Configuration -->
              <SessionConfigForm
                v-if="currentStep === 2"
                :tutor="selectedTutor"
                :current-step="currentStep"
                :selected-date-time="sessionData"
                :is-booking="isBookingSession"
                :initial-form-data="sessionData"
                @next-step="handleNextStep"
                @previous-step="handlePreviousStep"
                @session-configured="handleSessionConfigured"
              />
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import StudentHeader from "@/components/StudentHeader.vue";
import StudentStatusBanner from "@/components/Student/StudentStatusBanner.vue";
import TutorProfileCard from "@/components/Student/TutorProfileCard.vue";
import SessionConfigForm from "@/components/Student/SessionConfigForm.vue";
import DateTimeSelector from "@/components/Student/DateTimeSelector.vue";
import PreTestModal from "@/components/Student/PreTestModal.vue";
import { tutorService } from "@/services/tutorService";
import { sessionService } from "@/services/sessionService";
import { useToast } from "@/composables/useToast";
import { logError, logDebug, logInfo, logSuccess, logWarn } from "@/utils/secureLogger";
import { useStudentStatus } from "@/composables/useStudentStatus";
import { usePendingPostTest } from "@/composables/usePendingPostTest";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { db, auth } from "@/firebase";
import Swal from "sweetalert2";
import { eventBus, EVENTS } from "@/utils/eventBus";

const PRETEST_STORAGE_KEY = "elevated_pending_pretest";

const route = useRoute();
const router = useRouter();
const { showError, showWarning } = useToast();

// Student status composable
const { canBookSessions, isLoading: isLoadingStatus } = useStudentStatus();

// Pending post-test check
const {
  hasBlockingPendingPostTest,
  nextPendingPostTest,
  checkPendingPostTest,
  isLoading: isLoadingPendingTest,
} = usePendingPostTest();

// Reactive flag to control component visibility
const isCheckingAccess = ref(true);
const accessDenied = ref(false);

const selectedTutor = ref(null);
const currentStep = ref(1);
const isLoadingTutor = ref(false);
const isBookingSession = ref(false);

// Pre-test state
const showPreTest = ref(false);
const bookedSessionId = ref("");
const preTestCompleted = ref(false); // ✅ Flag to prevent double-handling

const sessionData = ref({
  dateTime: null,
  subject: "",
  learningGoals: "",
  mode: "",
  selectedWeekDate: null, // Store the week date when selection was made
});

// Temporary debug function - expose to window for easy UID retrieval
if (typeof window !== "undefined") {
  // @ts-ignore
  window.getMyUID = () => {
    const user = auth.currentUser;
    if (user) {
      console.log("%c🔑 Your Firebase UID:", "color: #667eea; font-size: 16px; font-weight: bold;");
      console.log(
        "%c" + user.uid,
        "color: #28a745; font-size: 14px; font-family: monospace; background: #f8f9fa; padding: 5px;"
      );
      console.log(
        "%c\nRun this command in PowerShell:",
        "color: #333; font-weight: bold; font-size: 14px;"
      );
      console.log(
        "%cpython check_rate_limit_sessions.py " + user.uid,
        "color: #0066cc; font-family: monospace; background: #f0f0f0; padding: 5px;"
      );
      return user.uid;
    } else {
      console.error("❌ Not logged in");
      return null;
    }
  };

  // ✅ TEST FUNCTION: Force open FileReport modal (for testing error modal report buttons)
  // @ts-ignore
  window.testReportButton = () => {
    console.log(
      "%c🧪 Testing Report Button",
      "color: #ff6b6b; font-size: 16px; font-weight: bold;"
    );
    try {
      eventBus.emit(EVENTS.OPEN_FILE_REPORT);
      console.log("%c✅ Event emitted successfully!", "color: #28a745; font-size: 14px;");
      console.log("The FileReport modal should now be open.");
    } catch (error) {
      console.error("%c❌ Failed to emit event:", "color: #dc3545; font-size: 14px;", error);
    }
  };

  // ✅ TEST FUNCTION: Simulate session creation error with Report button
  // @ts-ignore
  window.testSessionErrorModal = async () => {
    console.log(
      "%c🧪 Testing Session Error Modal with Report Button",
      "color: #ff6b6b; font-size: 16px; font-weight: bold;"
    );
    const result = await Swal.fire({
      icon: "error",
      title: "Session Creation Failed (TEST)",
      html: `
        <p>This is a test of the error modal with Report button.</p>
        <p class="mt-2 text-sm text-gray-600">Click "Report" to test the FileReport modal.</p>
      `,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      confirmButtonText: "Report",
    });

    // Wait for Swal to fully close before opening FileReport
    if (result.isConfirmed) {
      console.log(
        "%c✅ Report button clicked - emitting event",
        "color: #28a745; font-size: 14px;"
      );
      // Add delay to ensure Swal modal closes first
      await new Promise((resolve) => setTimeout(resolve, 300));
      eventBus.emit(EVENTS.OPEN_FILE_REPORT);
    } else {
      console.log("%c⚠️ Modal cancelled/dismissed", "color: #ffc107; font-size: 14px;");
    }
  };

  // ✅ TEST FUNCTION: Trigger REAL session creation error modal (from handlePreTestComplete)
  // @ts-ignore
  window.testRealSessionError = async () => {
    console.log(
      "%c🧪 Testing REAL Session Creation Error",
      "color: #ff6b6b; font-size: 16px; font-weight: bold;"
    );
    console.log("This will trigger the actual error modal from handlePreTestComplete()");

    // Set up fake pre-test data
    const fakePreTestData = {
      score: 8,
      totalQuestions: 10,
      answers: [],
    };

    // Set up fake session data (but we'll make it fail)
    sessionStorage.setItem(
      "pendingSessionData",
      JSON.stringify({
        tutorId: "test-tutor-id",
        subject: "Math",
        mode: "online",
        learningGoals: "Test learning goals",
        dateTime: { date: new Date(), timeSlot: { time: "2:00 PM" } },
      })
    );

    // Temporarily patch sessionService.createSession to throw an error
    const originalCreateSession = sessionService.createSession;
    sessionService.createSession = async () => {
      throw new Error("Test error: Session creation failed");
    };

    console.log("🔥 Triggering handlePreTestComplete with forced error...");

    try {
      await handlePreTestComplete(fakePreTestData);
    } catch (error) {
      console.log("Error caught (expected):", error);
    }

    // Restore original function after test
    setTimeout(() => {
      sessionService.createSession = originalCreateSession;
      console.log("✅ Restored original sessionService.createSession");
    }, 1000);
  };

  console.log(
    "%c🛠️ Test functions available:",
    "color: #667eea; font-size: 14px; font-weight: bold;"
  );
  console.log("  • window.getMyUID() - Get your Firebase UID");
  console.log("  • window.testReportButton() - Test direct event emission");
  console.log("  • window.testSessionErrorModal() - Test error modal with Report button");
  console.log("  • window.testRealSessionError() - Test REAL session creation error modal");
}

// Get tutor data from route params or state
onMounted(async () => {
  try {
    console.log("🔍 Starting access checks...");

    // Force check for pending post-tests immediately
    await checkPendingPostTest();

    // Wait for both student status and pending test check to fully load (with timeout)
    // Use a more efficient polling interval to reduce UI jank
    let maxWait = 20; // 4 seconds max (20 * 200ms)
    let waitCount = 0;

    while ((isLoadingStatus.value || isLoadingPendingTest.value) && waitCount < maxWait) {
      await new Promise((resolve) => setTimeout(resolve, 200)); // Check every 200ms instead of 100ms
      waitCount++;
    }

    if (waitCount >= maxWait) {
      console.error("⏰ Timeout waiting for status checks");
      // Continue anyway to avoid infinite loading
    }

    console.log("✅ Checks complete:", {
      hasBlockingPendingPostTest: hasBlockingPendingPostTest.value,
      canBookSessions: canBookSessions.value,
      nextPendingPostTest: nextPendingPostTest.value,
    });

    // Check for pending post-test blocking FIRST (higher priority)
    if (hasBlockingPendingPostTest.value && nextPendingPostTest.value) {
      console.log("🚫 Blocking due to pending post-test - redirecting to dashboard");
      accessDenied.value = true;
      isCheckingAccess.value = false;

      // Redirect immediately WITHOUT showing modal
      await router.push({ name: "student-sessions" });
      return;
    }

    // Check if student can book sessions
    if (!canBookSessions.value) {
      console.log("🚫 Cannot book sessions, redirecting to find-tutors");
      accessDenied.value = true;
      isCheckingAccess.value = false;
      await router.push({ name: "find-tutors" });
      return;
    }

    console.log("✅ Access granted");
    // Access granted - allow component to render
    isCheckingAccess.value = false;

    // Get tutor data from route params, query params, or state
    const tutorId = route.params.tutorId || route.query.tutorId;

    // Check for pending pre-test from previous session (page refresh/tab switch)
    const hasPendingPreTest = localStorage.getItem(PRETEST_STORAGE_KEY);

    if (tutorId) {
      // User is starting a NEW booking - clear any old pending pre-test data
      if (hasPendingPreTest) {
        logInfo("Clearing old pending pre-test - user starting new booking");
        localStorage.removeItem(PRETEST_STORAGE_KEY);
      }
    } else if (hasPendingPreTest) {
      // No tutorId but has pending pre-test = user refreshed during pre-test
      // Restore the pending pre-test modal
      await checkPendingPreTest();
    } else if (!tutorId) {
      // User arrived at booking page without a tutor selected
      // Check if they should be redirected to a completed booking confirmation
      const completedBookingData = sessionStorage.getItem("bookingConfirmationData");
      if (completedBookingData) {
        try {
          const parsed = JSON.parse(completedBookingData);
          // If we have valid confirmation data with a dateTime, redirect to confirmation
          if (parsed.dateTime && parsed.dateTime !== "") {
            router.push({
              name: "booking-confirmation",
              query: parsed,
            });
            return;
          }
        } catch (e) {
          logError("Error parsing completed booking data", e);
        }
      }
    } else {
      // User is starting a NEW booking (has tutorId), clear any old confirmation data
      sessionStorage.removeItem("bookingConfirmationData");
    }

    if (tutorId) {
      await loadTutorData(tutorId);
    } else {
      // Check if tutor data was passed via router state
      if (history.state?.tutor) {
        selectedTutor.value = history.state.tutor;
      } else {
        // Fallback when no tutor ID is provided
        // Redirect back to find tutors page since we need a tutor ID
        router.push({ name: "find-tutors" });
        return;
      }
    }
  } catch (error) {
    console.error("❌ Error in access check:", error);
    logError("Error during access check", error);
    // On error, allow access but log the issue
    isCheckingAccess.value = false;
  }
});

/**
 * Check for pending pre-test from localStorage
 * Restore modal state if user refreshed page or switched tabs
 */
const checkPendingPreTest = async () => {
  try {
    const pendingData = localStorage.getItem(PRETEST_STORAGE_KEY);

    if (!pendingData) {
      return; // No pending pre-test
    }

    const parsed = JSON.parse(pendingData);
    const { sessionId, sessionSubject, timestamp } = parsed;

    // Check if pre-test was already completed in Firestore
    const preTestRef = doc(db, "preTests", sessionId);
    const preTestDoc = await getDoc(preTestRef);

    if (preTestDoc.exists()) {
      // Pre-test already completed, clear localStorage
      logInfo("Pre-test already completed in Firestore, clearing localStorage", { sessionId });
      localStorage.removeItem(PRETEST_STORAGE_KEY);
      return;
    }

    // Check if the pending pre-test is not too old (24 hours max)
    const hoursSince = (Date.now() - timestamp) / (1000 * 60 * 60);
    if (hoursSince > 24) {
      logInfo("Pending pre-test expired (>24 hours), clearing", { sessionId });
      localStorage.removeItem(PRETEST_STORAGE_KEY);
      return;
    }

    // Restore modal state
    logInfo("Restoring pending pre-test modal", { sessionId, sessionSubject });
    bookedSessionId.value = sessionId;
    sessionData.value.subject = sessionSubject;

    // Fetch the actual session data from Firestore to rebuild confirmation data
    try {
      const sessionRef = doc(db, "sessions", sessionId);
      const sessionDoc = await getDoc(sessionRef);

      if (sessionDoc.exists()) {
        const session = sessionDoc.data();

        // Fetch tutor name
        let tutorName = "Tutor";
        if (session.tutorId) {
          try {
            const tutorRef = doc(db, "users", session.tutorId);
            const tutorDoc = await getDoc(tutorRef);
            if (tutorDoc.exists()) {
              const tutorData = tutorDoc.data();
              tutorName = `${tutorData.firstName || ""} ${tutorData.lastName || ""}`.trim();
            }
          } catch (error) {
            logError("Error fetching tutor details", error);
          }
        }

        // Rebuild confirmation data from Firestore session
        // Note: Firestore uses startAt/endAt timestamps, not dateTime
        const confirmationData = {
          dateTime: session.startAt ? session.startAt.toDate().toISOString() : "",
          endTime: session.endAt
            ? session.endAt.toDate().toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })
            : null,
          subject: session.subject || sessionSubject,
          mode: session.mode || "",
          location: session.location || "",
          tutorName: tutorName,
          tutorId: session.tutorId || "",
        };

        // Save to sessionStorage so it's available on confirmation page
        sessionStorage.setItem("bookingConfirmationData", JSON.stringify(confirmationData));
        logInfo("Booking confirmation data rebuilt from Firestore session", confirmationData);
      }
    } catch (error) {
      logError("Error fetching session from Firestore", error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      showError(
        `Unable to restore session details: ${errorMsg
          .replace(/^Error:\s*/i, "")
          .replace(/firebase/gi, "database")
          .replace(/firestore/gi, "database")}`
      );
    }

    showPreTest.value = true;
  } catch (error) {
    logError("Error checking pending pre-test", error);
    const errorMsg = error instanceof Error ? error.message : String(error);
    showWarning(
      `Could not restore your previous pre-test: ${errorMsg
        .replace(/^Error:\s*/i, "")
        .replace(/firebase/gi, "database")
        .replace(/firestore/gi, "database")}`
    );
    // Clear corrupted data
    localStorage.removeItem(PRETEST_STORAGE_KEY);
  }
};

const loadTutorData = async (tutorId) => {
  isLoadingTutor.value = true;

  try {
    const tutorData = await tutorService.getTutorById(tutorId);

    // ✅ Calculate actual completed sessions count
    let completedSessionsCount = 0;
    try {
      const { collection, query, where, getDocs } = await import("firebase/firestore");
      const sessionsRef = collection(db, "sessions");
      const completedQuery = query(
        sessionsRef,
        where("tutorId", "==", tutorId),
        where("sessionStatus", "==", "completed")
      );
      const completedSnapshot = await getDocs(completedQuery);
      completedSessionsCount = completedSnapshot.size;
      logDebug(`Tutor ${tutorId} has ${completedSessionsCount} completed sessions`);
    } catch (error) {
      logError("Error counting completed sessions", error);
      // Fall back to tutor document field if query fails
      completedSessionsCount = tutorData.totalSessions || tutorData.total_sessions || 0;
    }

    // Process subjects to ensure it's always an array
    let subjects = tutorData.subjects || ["General Tutoring"];
    if (typeof subjects === "string") {
      subjects = subjects.includes(",") ? subjects.split(",").map((s) => s.trim()) : [subjects];
    }

    // Process languages to ensure it's always an array
    let languages = tutorData.languages || ["English"];
    if (typeof languages === "string") {
      languages = languages.includes(",") ? languages.split(",").map((s) => s.trim()) : [languages];
    }

    // Format education display
    const formatEducation = (tutorData) => {
      // If education array exists and has at least one entry
      if (
        tutorData.education &&
        Array.isArray(tutorData.education) &&
        tutorData.education.length > 0
      ) {
        const edu = tutorData.education[0];
        const degree = edu.degree || tutorData.educationLevel || "";
        const fieldOfStudy = edu.fieldOfStudy || tutorData.fieldOfStudy || "";

        // Map degree codes to readable names
        const degreeMap = {
          "shs-current": "Senior High School (Current)",
          "shs-undergraduate": "Senior High School (Current)", // Legacy support
          "shs-graduate": "Senior High School (Graduate)",
          "college-undergraduate": "College Undergraduate",
          bachelor: "Bachelor",
          master: "Master's Degree",
          doctorate: "Doctorate/PhD",
          professional: "Professional Degree",
        };

        const degreeName = degreeMap[degree] || degree;

        // Format based on degree type
        if (degree === "bachelor" && fieldOfStudy) {
          return `Bachelor of ${fieldOfStudy}`;
        } else if (degree === "master" && fieldOfStudy) {
          return `Master's in ${fieldOfStudy}`;
        } else if (degree === "doctorate" && fieldOfStudy) {
          return `Doctorate in ${fieldOfStudy}`;
        } else if (fieldOfStudy) {
          return `${degreeName} - ${fieldOfStudy}`;
        } else {
          return degreeName;
        }
      }

      // Fallback to educationLevel and fieldOfStudy
      if (tutorData.educationLevel || tutorData.fieldOfStudy) {
        const degree = tutorData.educationLevel || "";
        const fieldOfStudy = tutorData.fieldOfStudy || "";

        const degreeMap = {
          "shs-current": "Senior High School (Current)",
          "shs-undergraduate": "Senior High School (Current)", // Legacy support
          "shs-graduate": "Senior High School (Graduate)",
          "college-undergraduate": "College Undergraduate",
          bachelor: "Bachelor",
          master: "Master's Degree",
          doctorate: "Doctorate/PhD",
          professional: "Professional Degree",
        };

        const degreeName = degreeMap[degree] || degree;

        // For Senior High School, show degree level with field of study
        if (
          (degree === "shs-current" ||
            degree === "shs-undergraduate" ||
            degree === "shs-graduate") &&
          fieldOfStudy
        ) {
          return `${degreeName} - ${fieldOfStudy}`;
        } else if (
          degree === "shs-current" ||
          degree === "shs-undergraduate" ||
          degree === "shs-graduate"
        ) {
          return degreeName;
        }

        // For Bachelor's and higher, only show field of study (no degree name)
        if (fieldOfStudy) {
          return fieldOfStudy;
        } else {
          return degreeName;
        }
      }

      return "Qualified educator";
    };

    // Format experience with proper singular/plural
    const formatExperience = (years) => {
      if (!years || years === 0) return `${years} years of teaching experience`;
      if (years === 1) return `${years} year of teaching experience`;
      return `${years} years of teaching experience`;
    };

    // Transform Firebase data to match component expectations
    selectedTutor.value = {
      id: tutorData.id,
      name: `${tutorData.firstName || ""} ${tutorData.lastName || ""}`.trim(),
      rating: tutorData.averageRating
        ? `${tutorData.averageRating.toFixed(1)}/5.0`
        : "No ratings yet",
      totalSessions: completedSessionsCount, // ✅ Use actual completed sessions count
      subjects: subjects,
      education: formatEducation(tutorData),
      experience: tutorData.experience || formatExperience(tutorData.experienceYears || 0),
      languages: languages,
      about: tutorData.bio || "Tutor dedicated to helping students succeed.",
      profileImage: tutorData.profileImage || tutorData.profile_image || null,
      hourlyRate: tutorData.hourlyRate || "Contact for rates",
      city: tutorData.city || null, // Add city field
    };
  } catch (error) {
    // Instead of redirecting, use fallback data and show warning with actual error
    const errorMsg = error instanceof Error ? error.message : String(error);
    const userFriendlyError = errorMsg
      .replace(/^Error:\s*/i, "")
      .replace(/firebase/gi, "database")
      .replace(/firestore/gi, "database")
      .trim();

    showWarning(
      `Unable to load complete tutor information: ${userFriendlyError}. Showing limited details.`,
      8000
    );

    selectedTutor.value = {
      id: tutorId,
      name: "Tutor (Loading Error)",
      rating: "4.5/5.0",
      totalSessions: 0,
      subjects: ["General Tutoring"],
      education: "Unable to load education info",
      experience: "Unable to load experience info",
      languages: ["English"],
      about:
        "There was an error loading this tutor's complete information. You can still proceed with booking.",
      profileImage: null,
      hourlyRate: "Contact for rates",
      city: null, // Add city field to fallback
    };
  } finally {
    isLoadingTutor.value = false;
  }
};

const handleNextStep = () => {
  if (currentStep.value < 2) {
    currentStep.value++;
  }
};

const handlePreviousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

const handleDateTimeSelected = (dateTimeData) => {
  sessionData.value.dateTime = dateTimeData;
  // Store the week date when selection was made
  sessionData.value.selectedWeekDate = dateTimeData.weekDate;
};

const handleSessionConfigured = async (configData) => {
  sessionData.value = { ...sessionData.value, ...configData };

  isBookingSession.value = true;

  try {
    // ✅ NEW FLOW: DON'T create session immediately
    // Store session data temporarily, create it AFTER pre-test completion

    // Prepare session data for LATER creation (after pre-test)
    const sessionToCreate = {
      tutorId: configData.tutorId || selectedTutor.value.id,
      subject: configData.subject,
      mode: configData.mode,
      learningGoals: configData.learningGoals,
      dateTime: sessionData.value.dateTime,
    };

    // Add location if provided (for face-to-face sessions)
    if (configData.location) {
      sessionToCreate.location = configData.location;
    }

    // Add materials if provided
    if (configData.materials && configData.materials.length > 0) {
      // ✅ FIX: Strip out originalFile before saving to Firestore
      // Firestore doesn't support File objects, only store metadata
      sessionToCreate.materials = configData.materials.map(
        ({ name, url, type, size, path, uploadedAt }) => ({
          name,
          url,
          type,
          size,
          path,
          uploadedAt,
          // originalFile is intentionally excluded (can't be stored in Firestore)
        })
      );
    }

    // ✅ NEW: Store session data for creation AFTER pre-test completion
    // Don't create session yet - wait until user completes pre-test
    sessionStorage.setItem("pendingSessionData", JSON.stringify(sessionToCreate));

    // Generate a temporary session ID for pre-test (will be replaced with real ID after creation)
    const tempSessionId = `temp_${Date.now()}_${auth.currentUser?.uid}`;
    bookedSessionId.value = tempSessionId;
    sessionData.value.subject = configData.subject;

    logInfo("🚫 SESSION NOT CREATED YET - Waiting for pre-test completion", {
      tempSessionId,
      willCreateAfterPreTest: true,
      storedInSessionStorage: true,
      firestoreSessionsCollection: "NOT MODIFIED YET",
    });

    console.log(
      "✋ IMPORTANT: No session saved to Firestore yet. Session will only be created AFTER user completes the pre-test."
    );

    // Save booking data to sessionStorage BEFORE showing pre-test modal
    // This ensures data persists if user refreshes during pre-test
    const confirmationData = {
      dateTime:
        sessionData.value.dateTime?.date && sessionData.value.dateTime?.timeSlot
          ? (() => {
              const { date, timeSlot } = sessionData.value.dateTime;

              // Parse the time slot to get hour and minute
              let hour = 0;
              let minute = 0;

              if (timeSlot.startTime) {
                // Use startTime if available (format: "14:00")
                const [h, m] = timeSlot.startTime.split(":").map(Number);
                hour = h;
                minute = m;
              } else if (timeSlot.time) {
                // Parse display time format (e.g., "2:00 PM")
                const timeMatch = timeSlot.time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
                if (timeMatch) {
                  hour = parseInt(timeMatch[1]);
                  minute = parseInt(timeMatch[2]);
                  if (timeMatch[3].toUpperCase() === "PM" && hour !== 12) {
                    hour += 12;
                  } else if (timeMatch[3].toUpperCase() === "AM" && hour === 12) {
                    hour = 0;
                  }
                }
              }

              return new Date(date.year, date.month, date.date, hour, minute).toISOString();
            })()
          : "",
      endTime: sessionData.value.dateTime?.timeSlot?.endTime || null,
      subject: configData.subject,
      mode: configData.mode,
      location: configData.location || "",
      learningGoals: configData.learningGoals || "", // Pass learning goals to pre-test
      tutorName:
        selectedTutor.value?.name ||
        selectedTutor.value?.displayName ||
        (selectedTutor.value?.firstName && selectedTutor.value?.lastName
          ? `${selectedTutor.value.firstName} ${selectedTutor.value.lastName}`
          : "Tutor"),
      tutorId: configData.tutorId || selectedTutor.value?.id,
    };
    sessionStorage.setItem("bookingConfirmationData", JSON.stringify(confirmationData));

    // Show pre-test modal instead of navigating immediately
    // Pass learning goals and materials to the modal for AI question generation
    sessionData.value.learningGoals = configData.learningGoals;
    // ✅ IMPORTANT: Pass full materials with originalFile to PreTest (memory-only, not saved to Firestore)
    sessionData.value.materials = configData.materials || [];
    showPreTest.value = true;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logError("Booking error", error);
    console.error("Full booking error details:", errorMessage, error);

    // Handle rate limiting errors specifically
    if (
      error instanceof Error &&
      (error.message.includes("Booking limit exceeded") ||
        error.message.includes("You can only book 3 sessions"))
    ) {
      logDebug("Showing rate limit warning toast");
      showWarning(error.message, 8000); // Show for 8 seconds
    } else if (
      error instanceof Error &&
      error.message.includes("You already have a session scheduled")
    ) {
      // Handle session overlap errors
      logDebug("Showing session overlap warning toast");
      showWarning(error.message, 8000); // Show for 8 seconds
    } else if (
      error instanceof Error &&
      (error.message.includes("must be 18 years") ||
        error.message.includes("Date of birth") ||
        error.message.includes("profile not found"))
    ) {
      // Handle age verification errors
      logDebug("Showing age verification error toast");
      showWarning(error.message, 10000); // Show for 10 seconds
    } else {
      // Show detailed error message instead of generic one
      logDebug("Showing detailed error toast");
      const userFriendlyError = errorMessage
        .replace(/^Error:\s*/i, "")
        .replace(/firebase/gi, "database")
        .replace(/firestore/gi, "database")
        .trim();
      showError(
        userFriendlyError ||
          "Unable to prepare session booking. Please check your information and try again."
      );
    }
  } finally {
    isBookingSession.value = false;
  }
};

/**
 * Handle pre-test completion
 * ✅ NEW: Create session AFTER pre-test completion, then navigate to confirmation
 */
const handlePreTestComplete = async (preTestData) => {
  logDebug("Pre-test completed, now creating session in Firestore", preTestData);

  // ✅ FIX: Set flag to prevent handlePreTestClose from running
  preTestCompleted.value = true;

  // ✅ FIX: DON'T close modal yet - keep it open during navigation to prevent flash
  // showPreTest.value = false; // REMOVED - will close after navigation

  // Clear pending pre-test from localStorage since it's now complete
  localStorage.removeItem(PRETEST_STORAGE_KEY);

  try {
    // ✅ CRITICAL: Retrieve pending session data and create session NOW
    const pendingSessionDataStr = sessionStorage.getItem("pendingSessionData");

    if (!pendingSessionDataStr) {
      logError("No pending session data found - session may have already been created");
      throw new Error("Session data not found. Please try booking again.");
    }

    const sessionToCreate = JSON.parse(pendingSessionDataStr);

    // Create the session in Firestore NOW (after pre-test completion)
    logInfo("✅ NOW CREATING SESSION IN FIRESTORE (pre-test was completed)");
    console.log("🔥 FIRESTORE WRITE: Creating session document in 'sessions' collection...");

    const realSessionId = await sessionService.createSession(sessionToCreate);

    logSuccess("✅ SESSION SUCCESSFULLY SAVED TO FIRESTORE", {
      sessionId: realSessionId,
      subject: sessionToCreate.subject,
      collection: "sessions",
      timestamp: new Date().toISOString(),
    });

    console.log("✅ Session saved to Firestore with ID:", realSessionId);

    // Update pre-test document with real session ID and link session to pre-test
    if (preTestData && bookedSessionId.value.startsWith("temp_")) {
      try {
        const preTestRef = doc(db, "preTests", bookedSessionId.value);
        const newPreTestRef = doc(db, "preTests", realSessionId);

        // Copy pre-test data to new document with real session ID
        const preTestDoc = await getDoc(preTestRef);
        if (preTestDoc.exists()) {
          await setDoc(newPreTestRef, {
            ...preTestDoc.data(),
            session_id: realSessionId, // Update to real session ID
          });

          // Delete the temporary pre-test document
          await deleteDoc(preTestRef);
          logSuccess("✅ Pre-test document updated with real session ID");

          // ✅ Link the session to the pre-test by updating session document
          // This happens AFTER the pre-test is successfully moved to the new ID
          try {
            const { updateDoc } = await import("firebase/firestore");
            const sessionRef = doc(db, "sessions", realSessionId);
            await updateDoc(sessionRef, {
              preTestId: realSessionId, // Link session to pre-test (pre-test ID = session ID)
            });
            logSuccess("✅ Session linked to pre-test", { sessionId: realSessionId });
          } catch (sessionUpdateError) {
            // Non-critical - session and pre-test are both saved successfully
            logWarn("Could not link session to pre-test (non-critical)", sessionUpdateError);
          }
        }
      } catch (err) {
        logWarn("Could not update pre-test document with real session ID", err);
        // Non-critical - session was created successfully
      }
    }

    // Clear pending session data
    sessionStorage.removeItem("pendingSessionData");

    // Build confirmation data
    const confirmationData = {
      dateTime:
        sessionData.value.dateTime?.date && sessionData.value.dateTime?.timeSlot
          ? (() => {
              const { date, timeSlot } = sessionData.value.dateTime;

              // Parse the time slot to get hour and minute
              let hour = 0;
              let minute = 0;

              if (timeSlot.startTime) {
                // Use startTime if available (format: "14:00")
                const [h, m] = timeSlot.startTime.split(":").map(Number);
                hour = h;
                minute = m;
              } else if (timeSlot.time) {
                // Parse display time format (e.g., "2:00 PM")
                const timeMatch = timeSlot.time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
                if (timeMatch) {
                  hour = parseInt(timeMatch[1]);
                  minute = parseInt(timeMatch[2]);
                  if (timeMatch[3].toUpperCase() === "PM" && hour !== 12) {
                    hour += 12;
                  } else if (timeMatch[3].toUpperCase() === "AM" && hour === 12) {
                    hour = 0;
                  }
                }
              }

              return new Date(date.year, date.month, date.date, hour, minute).toISOString();
            })()
          : "",
      endTime: sessionData.value.dateTime?.timeSlot?.endTime || null,
      subject: sessionData.value.subject,
      mode: sessionData.value.mode,
      location: sessionData.value.location || "",
      tutorName:
        selectedTutor.value?.name ||
        selectedTutor.value?.displayName ||
        (selectedTutor.value?.firstName && selectedTutor.value?.lastName
          ? `${selectedTutor.value.firstName} ${selectedTutor.value.lastName}`
          : "Tutor"),
      tutorId: sessionData.value.tutorId || selectedTutor.value?.id,
      sessionId: realSessionId, // Include real session ID
    };

    // Store booking data in sessionStorage to persist across page refreshes
    sessionStorage.setItem("bookingConfirmationData", JSON.stringify(confirmationData));

    // Navigate to confirmation page with session details
    await router.push({
      name: "booking-confirmation",
      query: confirmationData,
    });

    // ✅ FIX: Close modal AFTER navigation starts (prevents flash of BookSessionView)
    showPreTest.value = false;
  } catch (error) {
    logError("Failed to create session after pre-test completion", error);

    // Close modal on error too
    showPreTest.value = false;

    // Extract and format the actual error message
    const errorMessage = error instanceof Error ? error.message : String(error);
    const userFriendlyError = errorMessage
      .replace(/^Error:\s*/i, "") // Remove "Error:" prefix if present
      .replace(/firebase/gi, "database") // Replace technical terms
      .replace(/firestore/gi, "database")
      .trim();

    const modalResult = await Swal.fire({
      icon: "error",
      title: "Session Creation Failed",
      html: `
        <div class="text-left">
          <p class="mb-3">Your pre-test was completed successfully, but we encountered an issue creating your session:</p>
          <div class="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-3">
            <p class="text-sm text-red-900 font-semibold">📋 Error Details:</p>
            <p class="text-sm text-red-800 mt-2">${userFriendlyError}</p>
          </div>
          <p class="text-sm text-gray-600">Please try booking again or use the Report button to get help from our support team.</p>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      confirmButtonText: "Report",
      customClass: {
        htmlContainer: "text-left",
      },
    });

    // Handle result AFTER modal closes (await pattern, not .then())
    if (modalResult.isConfirmed) {
      console.log("✅ Report button clicked - opening FileReport modal");
      try {
        // ✅ FIX: Add small delay to ensure Swal modal is fully closed before opening FileReport
        // This prevents z-index conflicts where FileReport appears behind Swal backdrop
        await new Promise((resolve) => setTimeout(resolve, 300));
        eventBus.emit(EVENTS.OPEN_FILE_REPORT);
        console.log("✅ FileReport event emitted successfully");
      } catch (emitError) {
        console.error("❌ Failed to emit FileReport event:", emitError);
      }
    } else {
      console.log("⚠️ User cancelled/dismissed the error modal");
    }

    // Clear pending data and close modal
    sessionStorage.removeItem("pendingSessionData");
    showPreTest.value = false;
  }
};

/**
 * Handle pre-test modal close (user closed without completing or clicked "Go Back")
 * Note: This happens when user cancels validation warnings or goes back to edit
 */
const handlePreTestClose = () => {
  // ✅ FIX: If pre-test was completed, don't run this handler (prevent double navigation)
  if (preTestCompleted.value) {
    logDebug("Pre-test close handler skipped - test was already completed");
    preTestCompleted.value = false; // Reset flag
    return;
  }

  logInfo("🚫 Pre-test modal closed WITHOUT completion - NO session created");
  console.log("❌ User closed pre-test modal (clicked 'Go Back & Edit' or validation failed)");
  console.log("❌ NO session saved to Firestore - pending data remains in sessionStorage only");

  showPreTest.value = false;

  // ✅ Show informative message about session booking requirement
  Swal.fire({
    icon: "info",
    title: "Pre-Test Required",
    html: `
      <div class="text-left">
        <p class="mb-3">You've returned to the booking form.</p>
        <div class="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
          <p class="text-sm text-blue-900"><strong>📝 Remember:</strong></p>
          <p class="text-sm text-blue-800 mt-2">
            You must complete the pre-test assessment to successfully book your session.
            Your session will <strong>NOT be saved</strong> until you finish the assessment.
          </p>
        </div>
        <p class="text-sm text-gray-600 mt-3">
          Feel free to adjust your session details before trying again.
        </p>
      </div>
    `,
    confirmButtonText: "Got It",
    confirmButtonColor: "#3b82f6",
  });

  // ✅ FIX: Return user to step 2 (SessionConfigForm) to edit their data
  // Clear the temporary session ID and pending data
  bookedSessionId.value = "";

  // Clear pending session data since user exited
  sessionStorage.removeItem("pendingSessionData");

  // Go back to step 2 so they can see and edit their form
  currentStep.value = 2;

  logInfo("✅ User returned to session configuration form to edit their submission");

  // ✅ FIX: Don't create session if user is just going back to edit
  // User closed the modal to go back and fix learning goals or materials
  // They should be able to edit the session configuration and try again
  // Do NOT call handlePreTestComplete({}) here
};
</script>

<style scoped>
.student-page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url("/student-reg-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
  width: 100%;
  overflow-x: hidden;
}

/* Mobile optimizations */
@media (max-width: 640px) {
  .student-page-wrapper {
    background-size: cover;
    background-position: center top;
  }
}

/* Ensure content doesn't overflow on mobile */
@media (max-width: 768px) {
  .student-page-wrapper > div {
    max-width: 100vw;
    overflow-x: hidden;
  }
}
</style>
